import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Wrap,
  WrapItem,
  Heading,
  Text,
  Button,
  Select,
  Textarea,
  FormControl,
  FormLabel,
  Tag,
  Spinner,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../services/apiClient';
import {
  useAiQuota,
  useAiVoices,
  useAiSuggestions,
  useSubmitGeneration,
  useAiJob,
  useSaveClip,
  useDiscardGeneration,
} from '../hooks/useAiGeneration';
import { AI_CLIP_TYPES, AI_LANGUAGES, AI_TEXT_MAX, type AiClipType, type CreateAiGenerationRequest } from '../types';

export function AiGenerationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [clipType, setClipType] = useState<AiClipType>('announcement');
  const [language, setLanguage] = useState('uk-UA');
  const [voiceId, setVoiceId] = useState('');
  const [text, setText] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);

  const { data: quota } = useAiQuota();
  const { data: voicesData } = useAiVoices(language);
  const { data: suggestionsData } = useAiSuggestions(clipType, language, !jobId);
  const submit = useSubmitGeneration();
  const job = useAiJob(jobId, !!jobId);
  const save = useSaveClip();
  const discard = useDiscardGeneration();

  const voices = useMemo(() => voicesData?.voices ?? [], [voicesData]);

  useEffect(() => {
    if (voices.length && !voices.some((v) => v.voiceId === voiceId)) {
      setVoiceId(voices[0].voiceId);
    }
  }, [voices, voiceId]);

  const aiDisabled = quota ? !quota.enabled : false;
  const quotaExhausted = quota ? quota.used >= quota.limit : false;

  const buildRequest = (): CreateAiGenerationRequest => ({
    clipType,
    inputText: text.trim(),
    voiceId,
    language,
  });

  const mapError = (e: unknown) => {
    if (e instanceof ApiError) {
      if (e.status === 403) return t('aiGeneration.errorPlan');
      if (e.status === 429) return t('aiGeneration.errorQuota');
    }
    return e instanceof Error ? e.message : t('common.error', 'Something went wrong');
  };

  const handleGenerate = async () => {
    if (!text.trim() || !voiceId) return;
    try {
      const res = await submit.mutateAsync(buildRequest());
      setJobId(res.jobId);
    } catch (e) {
      toast({ status: 'error', title: mapError(e) });
    }
  };

  const handleDiscard = async () => {
    if (jobId) {
      try {
        await discard.mutateAsync(jobId);
      } catch {
        /* best-effort */
      }
    }
    setJobId(null);
  };

  const handleRegenerate = async () => {
    const req = buildRequest();
    if (jobId) {
      try {
        await discard.mutateAsync(jobId);
      } catch {
        /* best-effort */
      }
    }
    setJobId(null);
    try {
      const res = await submit.mutateAsync(req);
      setJobId(res.jobId);
    } catch (e) {
      toast({ status: 'error', title: mapError(e) });
    }
  };

  const handleSave = async () => {
    if (!jobId) return;
    try {
      await save.mutateAsync(jobId);
      toast({ status: 'success', title: t('aiGeneration.saved'), duration: 2500 });
      navigate('/library/clips');
    } catch (e) {
      toast({ status: 'error', title: mapError(e) });
    }
  };

  const jobData = job.data;
  const isGenerating =
    !!jobId &&
    (jobData?.status === 'pending' || jobData?.status === 'processing' || (job.isLoading && !jobData));
  const result = jobData?.status === 'completed' ? jobData.result : null;
  const isFailed = jobData?.status === 'failed';

  return (
    <Box maxW="640px">
      <Button
        as={RouterLink}
        to="/library/clips"
        variant="link"
        color="gray.500"
        leftIcon={<ArrowBackIcon />}
        mb={4}
      >
        {t('aiGeneration.back')}
      </Button>
      <Heading size="lg" color="gray.800" mb={1}>
        {t('aiGeneration.title')}
      </Heading>
      <Text color="gray.500" mb={6}>
        {t('aiGeneration.subtitle')}
      </Text>

      {aiDisabled ? (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={6} bg="white">
          <Text fontWeight="600" color="gray.700" mb={1}>
            {t('aiGeneration.gatedTitle')}
          </Text>
          <Text color="gray.500" fontSize="sm">
            {t('aiGeneration.gatedDescription')}
          </Text>
        </Box>
      ) : isGenerating ? (
        <VStack py={16} spacing={4}>
          <Spinner size="xl" color="brand.500" thickness="3px" />
          <Text color="gray.600" fontWeight="500">
            {t('aiGeneration.generating')}
          </Text>
          {jobData?.estimatedSeconds ? (
            <Text fontSize="sm" color="gray.400">
              {t('aiGeneration.estimated', { seconds: jobData.estimatedSeconds })}
            </Text>
          ) : null}
        </VStack>
      ) : result ? (
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={6} bg="white">
          <Heading size="md" color="gray.800" mb={1}>
            {result.title}
          </Heading>
          <HStack mb={4} spacing={3} color="gray.500" fontSize="sm">
            <Text>{result.voiceName}</Text>
            <Text>·</Text>
            <Text>{result.durationSeconds}s</Text>
          </HStack>
          <audio controls src={result.audioUrl} style={{ width: '100%' }} />
          <HStack mt={5} spacing={3}>
            <Button colorScheme="brand" onClick={handleSave} isLoading={save.isPending}>
              {t('aiGeneration.save')}
            </Button>
            <Button variant="outline" onClick={handleRegenerate} isLoading={submit.isPending || discard.isPending}>
              {t('aiGeneration.regenerate')}
            </Button>
            <Button variant="ghost" color="gray.500" onClick={handleDiscard}>
              {t('aiGeneration.discard')}
            </Button>
          </HStack>
        </Box>
      ) : isFailed ? (
        <VStack py={12} spacing={4} align="center">
          <Text color="red.500" fontWeight="500">
            {jobData?.error || t('aiGeneration.failed')}
          </Text>
          <Button variant="outline" onClick={handleDiscard}>
            {t('aiGeneration.tryAgain')}
          </Button>
        </VStack>
      ) : (
        <VStack spacing={5} align="stretch">
          <FormControl>
            <FormLabel>{t('aiGeneration.clipType')}</FormLabel>
            <Select value={clipType} onChange={(e) => setClipType(e.target.value as AiClipType)}>
              {AI_CLIP_TYPES.map((ct) => (
                <option key={ct} value={ct}>
                  {t(`aiGeneration.clipTypes.${ct}`)}
                </option>
              ))}
            </Select>
          </FormControl>

          <HStack spacing={4} align="flex-start">
            <FormControl>
              <FormLabel>{t('aiGeneration.language')}</FormLabel>
              <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                {AI_LANGUAGES.map((lng) => (
                  <option key={lng} value={lng}>
                    {t(`aiGeneration.languages.${lng}`)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>{t('aiGeneration.voice')}</FormLabel>
              <Select value={voiceId} onChange={(e) => setVoiceId(e.target.value)} placeholder={voices.length ? undefined : t('aiGeneration.loadingVoices')}>
                {voices.map((v) => (
                  <option key={v.voiceId} value={v.voiceId}>
                    {v.displayName}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>

          <FormControl>
            <Flex justify="space-between" align="baseline">
              <FormLabel mb={0}>{t('aiGeneration.text')}</FormLabel>
              <Text fontSize="xs" color={text.length > AI_TEXT_MAX ? 'red.500' : 'gray.400'}>
                {text.length}/{AI_TEXT_MAX}
              </Text>
            </Flex>
            <Textarea
              mt={1}
              value={text}
              maxLength={AI_TEXT_MAX}
              rows={4}
              placeholder={t('aiGeneration.textPlaceholder')}
              onChange={(e) => setText(e.target.value)}
            />
          </FormControl>

          {!!suggestionsData?.suggestions.length && (
            <Box>
              <Text fontSize="sm" color="gray.500" mb={2}>
                {t('aiGeneration.suggestions')}
              </Text>
              <Wrap spacing={2}>
                {suggestionsData.suggestions.map((s, i) => (
                  <WrapItem key={i}>
                    <Tag
                      colorScheme="brand"
                      variant="subtle"
                      cursor="pointer"
                      onClick={() => setText(s.slice(0, AI_TEXT_MAX))}
                    >
                      {s}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}

          {quota && (
            <HStack justify="space-between">
              <Badge colorScheme={quotaExhausted ? 'red' : 'gray'}>
                {t('aiGeneration.quota', { used: quota.used, limit: quota.limit })}
              </Badge>
            </HStack>
          )}

          <Button
            colorScheme="brand"
            size="lg"
            onClick={handleGenerate}
            isLoading={submit.isPending}
            isDisabled={!text.trim() || !voiceId || quotaExhausted}
          >
            {t('aiGeneration.generate')}
          </Button>
        </VStack>
      )}
    </Box>
  );
}

export default AiGenerationPage;
